<template>
    <div>
        <div class="filter filter__container">
            <div aria-live="polite" class="filter__announce-text">{{ filterLiveMessaging }}</div>
            <button ref="popoutPanelToggleButton" @click='toggleFilterOpenDialog(null)' class="filter__toggle" :class="{'focus-visible': galleryVersion == 'legacy', 'filter-open': isFiltersOpen, 'filter-closed': !isFiltersOpen}" aria-controls="FilterPopout" aria-haspopup="dialog" :aria-expanded="isFiltersOpen ? 'true' : 'false' " type="button" tabindex="0">
                <span class="desktop-show mobile-hide" v-html="getEnabledFiltersCount == 0 ? labels.filterToggleText : labels.filterToggleWithItemsEnabledText"></span>
                <span class="mobile-show desktop-hide" v-html="labels.filterControlsListBoxAriaLabel"></span>
            </button>
            <Transition name="filterpanel-transition">
            <div class="popout" ref="popoutPanel" id="FilterPopout" @focusout="onFocusOutOfPopup" @keydown.esc="toggleFilterOpenDialog(false)" :class="isFiltersOpen ? 'popout-open' : 'popout-closed'" v-show='isFiltersOpen' role="dialog"  :aria-hidden="!isFiltersOpen" :aria-label="labels.filterControlsAriaLabel">
                <div class="filter__mobile-header mobile-show desktop-hide">
                    <span v-html="getEnabledFiltersCount == 0 ? labels.filterToggleText : labels.filterToggleWithItemsEnabledText" class="mobile-show desktop-hide filter__add-filters--xs"></span>
                    <button @click='toggleFilterOpenDialog(false)' class="mobile-show desktop-hide filter__clear-filters--xs" :class="{'focus-visible': galleryVersion == 'legacy'}" type="button" tabindex="0" ref="filterPopupCloseButton"></button>
                </div>
                <ul class="filter__list" :aria-hidden="isFiltersOpen ? 'false' : 'true'" :aria-label="labels.filterControlsListBoxAriaLabel" role="listbox" aria-multiselectable="true">
                    <li class="filter__list-item" :class="{'focus-visible': galleryVersion == 'legacy'}" v-for="(category, index) in filters" v-bind:key="index" @keydown="(event) => onFilterKeyDown(event, category)" @click="toggleFilterOption(category)" :aria-selected="category.enabled.toString()" :aria-label="category.name" role="option" :tabindex="isFiltersOpen ? '0' : '-1'">
                        {{category.name }}
                    </li>

                    <li class="filter__list-item" :class="{'focus-visible': galleryVersion == 'legacy'}" v-if="hasMixedAssetData" @keydown="onShowImageOnlyKeyDown" @click="processShowImagesOnly" :aria-selected="showImagesOnly.toString()" role="option" :aria-label="labels.filterShowImagesOnlyText" :tabindex="isFiltersOpen ? '0' : '-1'">
                        {{labels.filterShowImagesOnlyText}}
                    </li>

                    <li class="filter__list-item" :class="{'focus-visible': galleryVersion == 'legacy'}" v-if="hasMixedAssetData" @keydown="onShowVideoOnlyKeyDown" @click="processShowVideoOnly" :aria-selected="showVideosOnly.toString()" role="option" :aria-label="labels.filterShowVideosOnlyText" :tabindex="isFiltersOpen ? '0' : '-1'">
                        {{labels.filterShowVideosOnlyText}}
                    </li>
                    <li>
                        <div class="filter__footer">
                            <button @click='toggleFilterOpenDialog(false)' class="mobile-show desktop-hide btn btn--primary" type="button" :tabindex="isFiltersOpen ? '0' : '-1'">{{labels.filterApplyMobileButtonText}}</button>
                            <button @click='toggleFilterOpenDialog(false)' class="mobile-hide desktop-show btn btn--primary" type="button" :tabindex="isFiltersOpen ? '0' : '-1'">{{ labels.filterApplyDesktopButtonText}}</button>

                            <button @click="() => { clearSetFilters(); toggleFilterOpenDialog(false); }" :disabled="getEnabledFiltersCount == 0" :aria-disabled="getEnabledFiltersCount == 0" class="btn btn--primary-outline" type="button" :tabindex="isFiltersOpen ? '0' : '-1'">{{ labels.filterClearButtonText }}</button>
                        </div>
                    </li>
                </ul>     
            </div>
            </Transition>
        </div>
    </div>
</template>

<script>
    import { mapState, mapMutations, mapGetters } from 'vuex';
    import { bus } from '../_event-bus';

    export default {
        name: 'GalleryFilter',
        data() {
            return {

            }
        },
        beforeDestroy() {
            window.matchMedia("(orientation: portrait)").removeEventListener("change", this.onScreenOrientationChange)
        },
        mounted() {
            // screen.orientation.addEventListener("orientationchange", this.onScreenOrientationChange); // Not yet supported
            window.matchMedia("(orientation: portrait)").addEventListener("change", this.onScreenOrientationChange)
        },

        computed: {
            ...mapGetters('gallery', [
                'getEnabledFilters',
                'getEnabledFiltersCount',
                'getFilteredItemsCount',
                'getTotalItemsCount',
            ]),

            ...mapState('gallery', [
                'isFiltersOpen',
                'filters',
                'showVideosOnly',
                'showImagesOnly',
                'galleryItems',
                'hasMixedAssetData',
                'galleryVersion',
                'cmsPreviewMode',
            ]),

            ...mapState('translations', [
                'labels',
            ]),

            filterLiveMessaging() {
                if (this.getTotalItemsCount == 0) {
                    return null;
                } else {
                    return this.labels.filterItemUpdate.replace('{0}', this.getFilteredItemsCount).replace('{1}', this.getTotalItemsCount);
                }
            },
        },

        methods: {
            ...mapMutations('gallery', [
                'updateFilterOpenState',
                'toggleFilterOpenState',
                'clearFilters',
                'toggleFilter',
                'toggleShowVideosOnly',
                'toggleShowImagesOnly',
                'updateFilteredList',
                'updateLoadingStatus',
            ]),

            processShowImagesOnly() {
                this.toggleShowImagesOnly();
                this.applyFilters();
            },

            processShowVideoOnly() {
                this.toggleShowVideosOnly();
                this.applyFilters();
            },

            toggleFilterOption(category) {
                this.toggleFilter(category);
                this.applyFilters();
            },

            toggleFilterOpenDialog(state) {
                if (state === null) { // If no paramater set, we want to toggle
                    this.toggleFilterOpenState();
                }
                else {
                    this.updateFilterOpenState(state);
                }

                // On mobile, we want to prevent the user from tabbing out
                // On desktop we want to hide the popup for the user if they click outside the popup
                if (window.innerWidth < 768) { // Mobile
                    if (this.isFiltersOpen) {
                        this.preventPageTabbing();
                        document.documentElement.classList.add('overflow-hidden', 'gallery-filters-open', 'gallery-filters-open-animation');
                    } else {
                        this.allowPageTabbing();
                        document.documentElement.classList.remove('overflow-hidden', 'gallery-filters-open');

                        setTimeout(() => {
                            document.documentElement.classList.remove('gallery-filters-open-animation');
                            document.documentElement.classList.add('gallery-filters-closing-animation');

                            setTimeout(() => {
                                document.documentElement.classList.remove('gallery-filters-closing-animation');
                            }, 500);

                        }, 801); // This timeout matches the animation time of the filter flyout
                    }
                } else { // Desktop
                    if (this.isFiltersOpen) {
                        setTimeout(() => {
                            document.addEventListener('click', this.onFocusOutOfPopup);
                        }, 50);
                    } else {
                        document.removeEventListener('click', this.onFocusOutOfPopup);
                    }
                }
            },

            setPreviousElementFocused(elem) {
                if (elem && elem.previousElementSibling) {
                    elem.previousElementSibling.focus();
                }
            },

            setNextElementFocused(elem) {
                if (elem && elem.nextElementSibling) {
                    elem.nextElementSibling.focus();
                }
            },

            onShowVideoOnlyKeyDown(event) {
                if (event.code === 'Space' || event.key === 'Enter') {
                    event.preventDefault();
                    this.processShowVideoOnly();
                } else if (event.code === 'ArrowUp') {
                    event.preventDefault();
                    this.setPreviousElementFocused(event.target);
                } else if (event.code === 'ArrowDown') {
                    event.preventDefault();
                    this.setNextElementFocused(event.target);
                }
            },

            onShowImageOnlyKeyDown(event) {
                if (event.code === 'Space' || event.key === 'Enter') {
                    event.preventDefault();
                    this.processShowImagesOnly();
                } else if (event.code === 'ArrowUp') {
                    event.preventDefault();
                    this.setPreviousElementFocused(event.target);
                } else if (event.code === 'ArrowDown') {
                    event.preventDefault();
                    this.setNextElementFocused(event.target);
                }
            },

            onFilterKeyDown(event, category) {
                if (window.innerWidth < 768) return;

                if (event.code === 'Space' || event.key === 'Enter') {
                    event.preventDefault();
                    this.toggleFilterOption(category);
                }
                else if (event.code === 'ArrowUp') {
                    event.preventDefault();
                    this.setPreviousElementFocused(event.target);
                } else if (event.code === 'ArrowDown') {
                    event.preventDefault();
                    this.setNextElementFocused(event.target);
                }
            },

            onFocusOutOfPopup(event) {
                event.stopPropagation();

                // When the user interacts with the page, we need to check if they are trying to close the popup
                // If they click outside the popup, or if the event fired is outside we should hide the popup

                const isMobile = window.innerWidth < 768;
                const isDesktop = window.innerWidth >= 768
                const eventNotInsidePopupPanel = this.$refs.popoutPanel.contains(event.relatedTarget) == false;
                const popupPanelToggleClick = this.$refs.popoutPanelToggleButton == event.target;
                const mobileNotPopupPanelClick = isMobile && this.$refs.popoutPanel != event.currentTarget;
                const isFilterItemOrList = event.target.classList.contains('filter__list-item') || event.target.classList.contains('filter__list');
                const isPopoutPanelContainer = event.target.classList.contains('popout');

                if (this.isFiltersOpen && eventNotInsidePopupPanel && 
                    (
                        (popupPanelToggleClick && isDesktop) || 
                        mobileNotPopupPanelClick ||
                        (popupPanelToggleClick == false && isDesktop && isFilterItemOrList == false && isPopoutPanelContainer == false))
                    ) {
                    this.updateFilterOpenState(false);
                    document.removeEventListener('click', this.onFocusOutOfPopup);
                }
            },

            clearSetFilters() {
                this.clearFilters();
                bus.$emit('ListingItemsChanged');
            },

            applyFilters() {
                const fullGalleryItems = this.galleryItems;
                const selectedFilters = this.getEnabledFilters;

                // We need to loop through all items to check if any of the caterogies associated with it, have a match against the cateogires that the user has  selected.
                const filteredItems = fullGalleryItems.map(item => {
                    let matchesAnyCategory = false;
                    let shouldBeShown = false;
                    const showVideosOnly = this.showVideosOnly;
                    const showImagesOnly = this.showImagesOnly;
                    const hasAssetFiltering = (showVideosOnly || showImagesOnly);
                    let resultAssetFiltering = false;

                    // Loop through every category assigned to the item to establish if there are any matches
                    // with any of the selected filters
                    item.categories.forEach(category => {
                        selectedFilters.forEach(filter => {
                            if (filter.enabled == true && filter.name === category) {
                                matchesAnyCategory = true;
                            }
                        });
                    });

                    // Check video/image only filters are enabled
                    if (hasAssetFiltering) {
                        if ((showVideosOnly && item.isVideo) || (showImagesOnly && item.isVideo === false)) {
                            resultAssetFiltering = true;
                        }
                    }

                    // If it matches category but has no asset filtering
                    if (matchesAnyCategory && hasAssetFiltering && resultAssetFiltering) {
                        shouldBeShown = true;
                    }

                    // If it matches category and does not have asset filtering
                    else if (matchesAnyCategory && hasAssetFiltering === false) {
                        shouldBeShown = true;
                    }

                    // If it has no category and but does have asset filtering and is enabled
                    else if (matchesAnyCategory == false && selectedFilters.length === 0 && hasAssetFiltering && resultAssetFiltering) {
                        shouldBeShown = true;
                    }

                    item.visible = shouldBeShown;

                    return item;
                });

                this.$nextTick(() => {
                    this.updateFilteredList(filteredItems);
                    bus.$emit('ListingItemsChanged');
                });
            },

            preventPageTabbing() {
                this.$refs.popoutPanel.addEventListener("keydown", this.trapUserTabbingToElem);

                setTimeout(() => {
                    this.$refs.filterPopupCloseButton.focus();
                }, 500);

            },

            allowPageTabbing() {
                this.$refs.popoutPanel.removeEventListener("keydown", this.trapUserTabbingToElem);
            },

            trapUserTabbingToElem(e) {
                const focusableEls = this.$refs.popoutPanel.querySelectorAll('a[href]:not([disabled]), button:not([disabled])');
                const firstFocusableEl = focusableEls[0];
                const lastFocusableEl = focusableEls[focusableEls.length - 1];
                
                const isTabPressed = (e.key === 'Tab' || e.keyCode === 9);
                if (!isTabPressed) {
                    return;
                }

                if (e.shiftKey) { // Shift + Tab
                    if (document.activeElement === firstFocusableEl) {
                        lastFocusableEl.focus();
                        e.preventDefault();
                    }
                } else { // Tab
                    if (document.activeElement === lastFocusableEl) {
                        firstFocusableEl.focus();
                        e.preventDefault();
                    }
                }
            },

            onScreenOrientationChange() {
                if (this.galleryVersion === 'modern' && this.cmsPreviewMode == false) {
                    this.updateLoadingStatus(true);
                    window.location.reload();
                    return;
                }

                // If the screen has changed rotation, we need to ensure we remove the old listeners and re-add the new ones

                if (window.innerWidth < 768) { // Mobile
                    document.removeEventListener('click', this.onFocusOutOfPopup); // Clear desktop event

                    if (this.isFiltersOpen) {
                        this.preventPageTabbing();
                        document.documentElement.classList.add('overflow-hidden', 'gallery-filters-open');
                    } else {
                        this.allowPageTabbing();
                        document.documentElement.classList.remove('overflow-hidden', 'gallery-filters-open');
                    }

                } else { // Desktop
                    document.documentElement.classList.remove('overflow-hidden', 'gallery-filters-open'); // Clear mobile class

                    if (this.isFiltersOpen) {
                        setTimeout(() => {
                            document.addEventListener('click', this.onFocusOutOfPopup);
                        }, 50);
                    } else {
                        document.removeEventListener('click', this.onFocusOutOfPopup);
                    }
                }
            }
        },
    }
</script>